import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { paymentSchema, type PaymentFormData } from '../schemas/paymentSchema'
import { useClients } from '@/features/clients/hooks/useClients'
import { useClientCustomers } from '@/features/customers/hooks/useCustomers'
import { useCustomerInvoices } from '@/features/invoices/hooks/useInvoices'
import { calculateCollectionFee } from '../domain/feeCalculator'
import { COLLECTION_FEE_PERCENTAGE } from '@/config/fees'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Textarea'
import { Card } from '@/components/ui/Card'
import { useState, useEffect } from 'react'
import { formatCurrency } from '@/lib/format'

interface PaymentFormProps {
  initialData?: any
  onSubmit: (data: PaymentFormData) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

export function PaymentForm({ initialData, onSubmit, onCancel, loading }: PaymentFormProps) {
  const { data: clients } = useClients()
  const { register, handleSubmit, watch, formState: { errors } } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      ...initialData,
      applied_to_baseline: false,
    },
  })

  const [calculatedFee, setCalculatedFee] = useState(0)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('')
  const selectedClientId = watch('client_id')
  const selectedCustomerId = watch('customer_id')
  const selectedAmount = watch('amount')

  const { data: customers } = useClientCustomers(selectedClientId)
  const { data: invoices } = useCustomerInvoices(selectedCustomerId)

  // Filter for open invoices (outstanding or partial_payment with balance remaining)
  const openInvoices = invoices?.filter(inv =>
    (inv.status === 'outstanding' || inv.status === 'partial_payment') &&
    inv.balance_due > 0
  ) || []

  useEffect(() => {
    if (selectedAmount) {
      const fee = calculateCollectionFee(selectedAmount)
      setCalculatedFee(fee)
    }
  }, [selectedAmount])

  const handleFormSubmit = async (data: PaymentFormData) => {
    const invoice_ids = selectedInvoiceId ? [selectedInvoiceId] : []
    await onSubmit({ ...data, collection_fee_owed: calculatedFee, invoice_ids } as any)
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Select
        label="Client"
        {...register('client_id')}
        options={clients?.map(c => ({ value: c.id, label: c.name })) || []}
        error={errors.client_id?.message}
        required
      />

      {selectedClientId && (
        <Select
          label="Customer"
          {...register('customer_id')}
          options={customers?.map(c => ({ value: c.id, label: c.name })) || []}
          error={errors.customer_id?.message}
          required
        />
      )}

      {selectedCustomerId && openInvoices.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Apply to Invoice (Optional)
          </label>
          <select
            value={selectedInvoiceId}
            onChange={(e) => setSelectedInvoiceId(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="">Select an invoice...</option>
            {openInvoices.map(inv => (
              <option key={inv.id} value={inv.id}>
                {inv.invoice_number} - {formatCurrency(inv.balance_due)} due
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {openInvoices.length} open invoice(s) available
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Payment Date"
          type="date"
          {...register('payment_date')}
          error={errors.payment_date?.message}
          required
        />
        <Input
          label="Amount"
          type="number"
          step="0.01"
          {...register('amount', { valueAsNumber: true })}
          error={errors.amount?.message}
          required
        />
      </div>

      <Select
        label="Payment Method"
        {...register('payment_method')}
        options={[
          { value: 'check', label: 'Check' },
          { value: 'wire', label: 'Wire Transfer' },
          { value: 'ach', label: 'ACH' },
          { value: 'credit_card', label: 'Credit Card' },
          { value: 'cash', label: 'Cash' },
        ]}
        error={errors.payment_method?.message}
        required
      />

      {calculatedFee > 0 && (
        <Card className="bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-primary-900">Collection Fee:</span>
            <span className="text-xl font-bold text-primary-900">{formatCurrency(calculatedFee)}</span>
          </div>
          <p className="text-xs text-primary-700 mt-1">
            {COLLECTION_FEE_PERCENTAGE}% of payment amount
          </p>
        </Card>
      )}

      <Textarea
        label="Notes"
        {...register('notes')}
        error={errors.notes?.message}
      />

      <div className="flex justify-end space-x-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={loading}>Record Payment</Button>
      </div>
    </form>
  )
}
